import { ButtonV2 } from "#src/commonV2/Button";
import { SectionCard } from "#src/commonV2/SectionCard";
import { TableV2 } from "#src/commonV2/Table";
import useFetchPaginated from "#src/hooks/useFetchPaginated";
import dlpService from "#src/services/endpoint/dlp";
import { PeripheralBlockType } from "#src/services/endpoint/dlp/enums";
import { IPeripheralPolicyItem } from "#src/services/endpoint/dlp/types";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { Switch } from "@lockerpm/design";
import { Eye, File06, Hand, Monitor01 } from "@untitled-ui/icons-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { EndpointDlpPeripheralPolicyConfigModal } from "./PolicyConfigModal";
import { BadgeV2 } from "#src/commonV2/badges/Badge";
import { DeviceTagType } from "#src/services/endpoint/enums";
import { BadgeSeeMoreV2 } from "#src/commonV2/badges/BadgeSeeMore";

export const EndpointDlpExternalStoragePeripheralPolicy = () => {
  const { t, i18n } = useTranslation("endpoint", {
    keyPrefix: "dlp.externalStorage.peripheralPolicy",
  });

  const { workspaceId } = useParams<"workspaceId">();

  if (!workspaceId) {
    return null;
  }

  // --------------- STATES ---------------

  const [configuringPolicy, setConfiguringPolicy] =
    useState<IPeripheralPolicyItem | null>(null);

  // --------------- HOOKS ---------------

  const fetchParams = useMemo(() => [workspaceId], [workspaceId]);

  const {
    list: policyList,
    isLoading,
    updateData: updatePolicyList,
  } = useFetchPaginated(dlpService.listPeripheralPolicies, fetchParams);

  // --------------- METHODS ---------------

  const getPeripheralBlockTypeBadge = (
    type: string | null,
    enabled: boolean
  ) => {
    if (type === PeripheralBlockType.READ)
      return (
        <div
          className={`px-2 py-1 flex items-center gap-1 whitespace-nowrap bg-purple-v2-50 border border-purple-v2-200 text-purple-v2-700 rounded-full text-xs font-medium ${enabled ? "" : "opacity-40"}`}
        >
          <Eye className="h-3 w-3 min-w-3 text-purple-v2" />
          {t("type.read")}
        </div>
      );
    if (type === PeripheralBlockType.WRITE)
      return (
        <div
          className={`px-2 py-1 flex items-center gap-1 whitespace-nowrap bg-error-v2-50 border border-error-v2-200 text-error-v2-700 rounded-full text-xs font-medium ${enabled ? "" : "opacity-40"}`}
        >
          <div className="h-2 w-2 min-w-2 bg-error-v2 rounded-full" />
          {t("type.write")}
        </div>
      );
    if (type === PeripheralBlockType.READ_WRITE)
      return (
        <div
          className={`px-2 py-1 flex items-center gap-1 whitespace-nowrap bg-purple-v2-50 border border-purple-v2-200 text-purple-v2-700 rounded-full text-xs font-medium ${enabled ? "" : "opacity-40"}`}
        >
          <Hand className="h-3 w-3 min-w-3 text-gray-blue-v2" />
          {t("type.readAndWrite")}
        </div>
      );
    return (
      <div
        className={`px-2 py-1 gap-1 bg-gray-v2-50 border border-gray-v2-200 rounded-full ${enabled ? "" : "opacity-40"}`}
      >
        {type}
      </div>
    );
  };

  const disablePolicy = (policy: IPeripheralPolicyItem) => {
    dlpService
      .updatePeripheralPolicies(workspaceId, policy.id, {
        enabled: false,
        sources: policy.sources.map((item) => ({
          id: item.id.toString(),
          type: item.type,
        })),
        exclude: policy.exclude.map((item) => ({
          id: item.id.toString(),
          type: item.type,
        })),
        block_type: policy.block_type ?? "",
        all_hosts: policy.all_hosts,
      })
      .then(() => {
        updatePolicyList();
      })
      .catch(apiErrorHandler);
  };

  // --------------- COMPUTED ---------------

  // --------------- EFFECTS ---------------

  // --------------- RENDER ---------------

  return (
    <>
      <SectionCard
        header={
          <div className="flex items-center gap-2">
            <File06 className="h-5 w-5 min-w-5 text-gray-v2" />
            {t("title")}
          </div>
        }
      >
        <TableV2<IPeripheralPolicyItem>
          data={policyList ?? []}
          getRowKey={(item) => item.id}
          isLoading={isLoading}
          columns={[
            {
              title: t("headers.blockPolicy"),
              render: (item) =>
                i18n.language === "vi" ? item.type.name.vi : item.type.name.en,
              gridTemplateValue: "2fr",
            },
            {
              title: t("headers.blockType"),
              render: (item) =>
                getPeripheralBlockTypeBadge(item.block_type, item.enabled),
              gridTemplateValue: "minmax(9rem, 1fr)",
            },
            {
              title: t("headers.appliedDevices"),
              render: (item) => (
                <>
                  {/* First 2 applied devices */}
                  {item.sources.slice(0, 2).map((item) => (
                    <BadgeV2
                      key={item.id}
                      name={item.name}
                      defaultIcon={item.type === DeviceTagType.TAG}
                      leftIcon={
                        item.type === DeviceTagType.HOST ? (
                          <Monitor01 className="h-4 w-4 min-w-4" />
                        ) : undefined
                      }
                    />
                  ))}
                  {/* First 2 applied devices end */}

                  {/* If there are more, show this "see more" */}
                  {item.sources.length > 2 && (
                    <BadgeSeeMoreV2
                      badges={item.sources.slice(2).map((item) => (
                        <BadgeV2
                          key={item.id}
                          name={item.name}
                          defaultIcon={item.type === DeviceTagType.TAG}
                          leftIcon={
                            item.type === DeviceTagType.HOST ? (
                              <Monitor01 className="h-4 w-4 min-w-4" />
                            ) : undefined
                          }
                        />
                      ))}
                    />
                  )}
                </>
              ),
              gridTemplateValue: "3fr",
              overwriteWrapperProps: { className: "gap-x-1" },
            },
            {
              title: t("headers.excluded"),
              render: (item) => (
                <>
                  {/* First 2 applied devices */}
                  {item.exclude.slice(0, 2).map((item) => (
                    <BadgeV2
                      key={item.id}
                      name={item.name}
                      defaultIcon={item.type === DeviceTagType.TAG}
                      leftIcon={
                        item.type === DeviceTagType.HOST ? (
                          <Monitor01 className="h-4 w-4 min-w-4" />
                        ) : undefined
                      }
                    />
                  ))}
                  {/* First 2 applied devices end */}

                  {/* If there are more, show this "see more" */}
                  {item.exclude.length > 2 && (
                    <BadgeSeeMoreV2
                      badges={item.exclude.slice(2).map((item) => (
                        <BadgeV2
                          key={item.id}
                          name={item.name}
                          defaultIcon={item.type === DeviceTagType.TAG}
                          leftIcon={
                            item.type === DeviceTagType.HOST ? (
                              <Monitor01 className="h-4 w-4 min-w-4" />
                            ) : undefined
                          }
                        />
                      ))}
                    />
                  )}
                </>
              ),
              gridTemplateValue: "3fr",
              overwriteWrapperProps: { className: "gap-x-1" },
            },
            {
              title: t("headers.status"),
              titleAlign: "center",
              render: (item) => (
                <Switch
                  checked={item.enabled}
                  onChange={(value) => {
                    if (value) {
                      setConfiguringPolicy(item);
                    } else {
                      disablePolicy(item);
                      updatePolicyList();
                    }
                  }}
                />
              ),
              gridTemplateValue: "minmax(6rem, 1fr)",
              overwriteWrapperProps: {
                className: "justify-center",
              },
            },
            {
              title: null,
              render: (item) => (
                <ButtonV2
                  variant="tertiary"
                  onClick={() => {
                    setConfiguringPolicy(item);
                  }}
                  disabled={!item.enabled}
                >
                  {t("button.config")}
                </ButtonV2>
              ),
              gridTemplateValue: "minmax(7rem, 1fr)",
              overwriteWrapperProps: {
                className: "justify-center",
              },
            },
          ]}
        />
      </SectionCard>
      <EndpointDlpPeripheralPolicyConfigModal
        workspaceId={workspaceId}
        open={configuringPolicy !== null}
        onClose={() => {
          setConfiguringPolicy(null);
        }}
        onRefresh={updatePolicyList}
        configuringPolicy={configuringPolicy}
      />
    </>
  );
};
